import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import AppBar from "../../components/layout/AppBar";
import ChatBotBar from "../../components/layout/ChatBotBar";
import Button from "../../components/common/Button";
import CircularLoader from "../../components/common/CircularLoader";
import CustomDropDownSelect from "../../components/common/CustomDropDown";
import CommonInputFiled from "../../components/common/InputField";
import uploadicon from "../../assets/svg/upload-icon.svg";
import deleteIcon from "../../assets/svg/chat-bot/delete-icon.svg";

import { apiRequest } from "../../services/api_services/ApiServices";

import "react-toastify/dist/ReactToastify.css";

const INITIAL_QUESTIONS = Array.from({ length: 1 }, (_, index) => ({
  id: index + 1,
  question: "",
  answer: "",
}));

const NewBotCreation = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [botName, setBotName] = useState("");
  const [application, setApplication] = useState("");
  const [applications, setApplications] = useState([]);

  const [questions, setQuestions] = useState(INITIAL_QUESTIONS);
  const [excelBase64, setExcelBase64] = useState("");
  const [excelFileName, setExcelFileName] = useState("");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await apiRequest({
        method: "GET",
        url: "app_list",
      });

      if (!response?.success) {
        toast.error(response?.message || "Failed to fetch applications");
        return;
      }

      const activeApps =
        response.data?.data
          // ?.filter((app) => app.status)
          .map((app) => ({
            value: app.id,
            label: app.name,
          })) || [];

      setApplications(activeApps);
      if (activeApps.length) setApplication(activeApps[0].value);
    } catch {
      toast.error("Application list error");
    }
  };

  const handleAddQuestion = () => {
    if (questions.length > 0) {
      const lastQuestion = questions[questions.length - 1];
      if (!lastQuestion.question.trim() || !lastQuestion.answer.trim()) {
        toast.error("fill required question and answer");
        return;
      }
    }

    setQuestions((prev) => [
      ...prev,
      { id: prev.length + 1, question: "", answer: "" },
    ]);
  };

  const handleRemoveQuestion = (index) => {
    setQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleQuestionChange = (index, field, value) => {
    setQuestions((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast.error("Please upload a valid Excel file");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(",")[1];
      setExcelBase64(base64);
      setExcelFileName(file.name);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleBotCreation = async () => {
    const validQuestions = questions.filter(
      (q) => q.question.trim() && q.answer.trim(),
    );

    if (!botName.trim()) {
      toast.error("Bot name is required");
      return;
    }

    setLoading(true);

    const payload = {
      bot_name: botName,
      application_id: application,
      questions: validQuestions.map(({ question, answer }) => ({
        question,
        answer,
      })),
      ...(excelBase64 && { excel_base64: excelBase64 }),
    };

    try {
      const response = await apiRequest({
        method: "Post",
        url: "bot",
        data: payload,
      });

      if (!response?.success) {
        toast.error(response?.message || "Bot creation failed");
        return;
      }

      toast.success(response.message || "Bot created successfully");

      resetForm();
      navigate("/admin_setting", { state: { selectedTab: "Chatbots" } });
    } catch {
      toast.error("Failed to create bot. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setBotName("");
    setApplication("");
    setExcelBase64("");
    setQuestions(INITIAL_QUESTIONS);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <AppBar title="Settings" onClick={() => navigate("/")} />

      <ChatBotBar
        title="New bot"
        leftText="Clear"
        rightText={
          loading ? <CircularLoader size={16} color="border-white" /> : "Save"
        }
        onClickPop={() =>
          navigate("/admin_setting", { state: { selectedTab: "Chatbots" } })
        }
        onClickLeft={resetForm}
        onClickRight={handleBotCreation}
      />

      <div className="flex-1 px-6 py-6 bg-[#F8F9FB]">
        <div className="bg-white rounded-lg p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Bot details</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium">
                Bot Name <span className="text-red-500"></span>
              </label>
              <CommonInputFiled
                value={botName}
                onChange={(e) => setBotName(e.target.value)}
                placeholder="Enter here..."
                className="mt-2 w-full border border-[#004BA8] rounded-md px-4 py-2 outline-none"
              />
            </div>

            <div>
              <label className="text-sm font-medium pb-2">Application</label>
              <CustomDropDownSelect
                options={applications}
                value={application}
                onChange={setApplication}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6">
          <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
            <h2 className="text-xl font-semibold">Questions</h2>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
            />

            <div className="flex flex-wrap gap-3 items-start">
              <Button
                label="+ Add"
                onClick={handleAddQuestion}
                color="bg-gray-100 text-black"
              />

              <div className="flex flex-col gap-1 items-center">
                <button
                  onClick={() => fileInputRef.current.click()}
                  className={`px-3 py-2 rounded-md font-medium flex gap-2 justify-center items-center transition duration-200 ${
                    excelBase64
                      ? "bg-[#004BA8] text-white"
                      : "bg-blue-100 text-[#004BA8]"
                  }`}
                >
                  {excelBase64 ? (
                    <></>
                  ) : (
                    <img src={uploadicon} alt="upload" className="w-5 h-5" />
                  )}
                  {excelBase64 ? "Excel Uploaded" : "Excel Upload"}
                </button>
                {excelFileName && (
                  <span className="text-xs text-gray-500 font-medium">{excelFileName}</span>
                )}
              </div>

              {excelBase64 === "" ? (
                <></>
              ) : (
                <Button
                  label="Remove Excel"
                  variant="remove"
                  onClick={() => {
                    setExcelBase64("");
                    setExcelFileName("");
                  }}
                />
              )}
            </div>
          </div>

          {questions.map((q, index) => (
            <div
              key={q.id}
              className="flex flex-col md:grid md:grid-cols-12 gap-2 md:gap-4 md:items-center mb-6 md:mb-3"
            >
              <div className="md:col-span-1 text-sm md:text-base font-semibold text-gray-700">{index + 1}.</div>

              <CommonInputFiled
                className="w-full md:col-span-5"
                placeholder="Enter question..."
                value={q.question}
                onChange={(e) =>
                  handleQuestionChange(index, "question", e.target.value)
                }
              />

              <div className="w-full md:col-span-6 flex items-center gap-3">
                <CommonInputFiled
                  className="flex-1 w-full"
                  placeholder="Enter answer..."
                  value={q.answer}
                  onChange={(e) =>
                    handleQuestionChange(index, "answer", e.target.value)
                  }
                />
                {questions.length > 1 && (
                  <div
                    className="w-10 h-10 flex items-center justify-center cursor-pointer rounded-full hover:bg-red-100 transition-colors"
                    onClick={() => handleRemoveQuestion(index)}
                  >
                    <img src={deleteIcon} alt="delete" className="w-6 h-6" />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewBotCreation;
